<script setup lang="ts">
import {
  ref, nextTick, watch, computed, onMounted, useAttrs,
} from 'vue';
import useEmitter from '@/composables/emitter';

type SecurityCodeInputSize = 'small' | 'medium' | 'large';

interface Props {
  length ?: number;
  size ?: SecurityCodeInputSize;
  runatId ?: string | null;
  disableFocusOnMount ?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  length: 6,
  size: 'medium',
  runatId: null,
});

const getInitialCodeArr = () => Array(props.length);

const attrs = useAttrs();
const hiddenAttrs = {
  name: typeof attrs.name === 'string' ? attrs.name : 'code',
};
const codes = ref<string[]>(getInitialCodeArr());
const inputRefs = ref<HTMLElement[]>([]);
const runatInput = ref();
const codesAsString = computed(() => codes.value.join(''));
const formCompleted = computed(() => codesAsString.value.length === props.length);
const emitter = useEmitter();

const emit = defineEmits(['key-updated', 'validate']);

const handleSubmit = () : void => {
  emit('key-updated', codesAsString.value);
  emitter.emit('ConnxSecurityCodeInput:key-updated', codesAsString.value);

  if (runatInput.value) {
    runatInput.value.value = codesAsString.value;
  }
};

const handleInput = (target : any, index : number) : void => {
  let { value } = target;
  if (value.length > 1) {
    const chars = target.value.split('');
    value = chars[chars.length - 1];
  }
  codes.value[index] = value;
  /* eslint-disable-next-line no-param-reassign */
  target.value = value;
  if (value !== '' && inputRefs.value[index + 1]) {
    nextTick(() => {
      inputRefs.value[index + 1].focus();
    });
  }
};

const handleKeyupDelete = (target : any) : void => {
  if (target.previousElementSibling) {
    target.previousElementSibling.focus();
  }
};

const handlePaste = ($event : ClipboardEvent) : void => {
  $event.stopPropagation();
  $event.preventDefault();
  const value = $event.clipboardData?.getData('text');
  if (value !== undefined && !Number.isNaN(parseInt(value, 10))) {
    const stringArr = value.split('');
    for (const [index, num] of stringArr.entries()) {
      if (index < codes.value.length) {
        codes.value[index] = num;
      }
    }

    nextTick(() => {
      inputRefs.value[codes.value.length - 1].focus();
    });
  }
};

watch(() => [...codes.value], () : void => {
  if (formCompleted.value) {
    handleSubmit();
  } else {
    emit('key-updated', null);
    emitter.emit('ConnxSecurityCodeInput:key-updated', null);
  }
});

const clearCodes = () : void => {
  codes.value = getInitialCodeArr();
};

const handleKeyupEnter = () : void => {
  if (formCompleted.value) {
    emit('validate');
    emitter.emit('ConnxSecurityCodeInput:validate');
  }
};

emitter.on('ConnxSecurityCodeInput:clearCodes', clearCodes);

defineExpose({
  clearCodes,
  handleSubmit,
  handleInput,
  handleKeyupDelete,
  handleKeyupEnter,
  handlePaste,
  codes,
});

onMounted(() => {
  nextTick(() => {
    if (!props.disableFocusOnMount) {
      inputRefs.value[0].focus();
    }
    if (props.runatId && document.getElementById(props.runatId)) {
      runatInput.value = document.getElementById(props.runatId) as HTMLInputElement;
    }
  });
});

</script>

<template>
  <div
    ref="form"
    class="
      tw-flex
      tw-justify-center
      tw-mx-auto
    "
  >
    <input
      type="hidden"
      :name="hiddenAttrs.name"
      :value="codesAsString"
    >
    <input
      v-for="(code, index) in codes"
      ref="inputRefs"
      :key="index"
      :value="code"
      :disabled="index !== 0 && !codes[index - 1]"
      class="
        tw-h-14
        tw-text-2xl
        tw-rounded
        tw-text-center
        tw-font-semibold
        tw-bg-connx-white
        tw-p-2
        tw-w-full
        tw-mx-0.5
        tw-max-w-20
        tw-border
        tw-border-solid
      "
      :class="{
        'sm:tw-mx-2': size !== 'small',
      }"
      type="number"
      pattern="[0-9]*"
      inputtype="numeric"
      min="0"
      max="9"
      aria-label="Verification code"
      @paste="handlePaste($event)"
      @input="handleInput($event.target, index)"
      @keyup.delete="handleKeyupDelete($event.target)"
      @keyup.enter="handleKeyupEnter()"
    >
  </div>
</template>
